import React, { ReactNode } from 'react'

import {
  Anchor,
  Button,
  Checkbox,
  Divider,
  Group,
  Modal,
  Select,
  Stack,
  Switch,
  Table,
  Text,
} from '@mantine/core'
import { useForm } from '@mantine/form'
import { observer } from 'mobx-react-lite'
import { flushSync } from 'react-dom'

import {
  getRevisionsState,
  RevisionMarkOption,
  type RevisionSet,
  RevisionsState,
  showRevisionMarkOptions,
} from '#choo-app/lib/editor/plugins/revisions'
import { FaIcon } from '#components/FaIcon'
import { Toast } from '#components/Toast'
import { useEditor, useMst } from '#hooks'
import { nido } from '#util'
import { SelectList } from '#util/SelectList'
import { nidoErrorSchema } from '#util/zodSchemas/index'

import { ModalForm } from '../ModalForm'

import { RevisionsTable, type WithKey } from './RevisionsTable'

import styles from './RevisionsModal.module.css'

interface RevisionsModalProps {
  opened: boolean
  onClose(): void
  onConfirm(values: FormState): void
  errorMessage?: ReactNode
  initialRevisionState?: RevisionsState
  changeTrackingActive?: boolean
  loading?: boolean
}

interface FormState extends RevisionsTable.State {
  enabled: boolean
  markOption: RevisionMarkOption
  showPageColors: boolean
}

const addKey = makeKeyable('available-revision')

export const RevisionsModal: React.FC<RevisionsModalProps> = (props) => {
  'use memo'

  return (
    <Modal
      title="Revisions Preferences"
      opened={props.opened}
      onClose={props.onClose}
      closeOnEscape={false}
      size="auto"
    >
      <RevisionsForm {...props} />
    </Modal>
  )
}

function RevisionsForm(props: RevisionsModalProps) {
  'use memo'

  const { onClose, onConfirm, initialRevisionState } = props
  const scrollContainerRef = React.useRef<HTMLDivElement>(null)
  const tableRef = React.useRef<RevisionsTable.Handle>(null)
  const form = useForm<FormState>({
    initialValues: initialFormState(initialRevisionState),
  })

  function handleNewRevisionSet() {
    const newRevisionSet: RevisionSet = {
      label: `New Rev.`,
      isFullDraft: false,
      mark: '*',
      textColor: '#000000',
      pageColor: '#FFFFFF',
    }

    const { available } = form.values
    if (Array.isArray(available)) {
      flushSync(() => {
        form.setFieldValue('available', [...available, addKey(newRevisionSet)])
      })
    }

    scrollToBottom(scrollContainerRef)
    tableRef.current?.selectLastLabel()
  }

  function handleEnabledChange(enabled: boolean) {
    form.setFieldValue('enabled', enabled)

    if (!enabled && form.values.available instanceof SelectList) {
      form.setFieldValue('available', Array.from(form.values.available))
    }
  }

  return (
    <ModalForm
      disabled={Boolean(props.errorMessage) || !form.isDirty()}
      loading={props.loading}
      confirmLabel="Save"
      cancelLabel="Cancel"
      onConfirm={() => onConfirm(form.values)}
      onClose={onClose}
      errorMessage={props.errorMessage}
    >
      <Stack gap={10}>
        <Group gap={20} align="flex-start" wrap="nowrap">
          <Stack gap={40} pt={10} w={260} className={styles['left-panel']}>
            <Stack gap={15}>
              <Group justify="space-between">
                <Text fw="bold">Revision Mode</Text>
                <Switch
                  aria-label="Revision mode"
                  checked={form.values.enabled}
                  disabled={Boolean(
                    props.changeTrackingActive && !form.values.enabled,
                  )}
                  onChange={(event) =>
                    handleEnabledChange(event.currentTarget.checked)
                  }
                />
              </Group>
              {props.changeTrackingActive ? (
                <Toast
                  type="warning"
                  dismissable={false}
                  message={
                    <Text>
                      <Text span>
                        To enable Revision mode, turn off Change-tracking mode
                        from the
                      </Text>{' '}
                      <Text span textWrap="nowrap">
                        <i className="fa-solid fa-plus-minus"></i>{' '}
                        <Text fw="bold" span>
                          Changes
                        </Text>
                      </Text>{' '}
                      <Text span>panel.</Text>{' '}
                      <Anchor
                        target="_blank"
                        href="https://help.scripto.live/en/articles/11498574-change-tracking-mode"
                      >
                        Learn more.
                      </Anchor>
                    </Text>
                  }
                />
              ) : form.values.enabled ? (
                <RevisionStatusMessage
                  initiallyEnabled={initialRevisionState?.enabled}
                  values={form.values}
                />
              ) : (
                <Text c="dimmed">Revision mode is off.</Text>
              )}
            </Stack>
            <Stack gap={2}>
              <Text fw="bold">Show marks and text colors</Text>
              <Select
                data={showRevisionMarkOptions}
                {...form.getInputProps('markOption')}
              />
            </Stack>
            <Group gap={10}>
              <Checkbox
                label="Show page colors"
                fw="bold"
                {...form.getInputProps('showPageColors', { type: 'checkbox' })}
              />
            </Group>
          </Stack>
          <Table.ScrollContainer
            scrollAreaProps={{ viewportRef: scrollContainerRef }}
            minWidth={700}
            maxHeight="calc(max(100vh - 350px, 200px))"
          >
            <RevisionsTable
              ref={tableRef}
              revisions={form.values}
              onChange={form.setValues}
              selectionDisabled={!form.values.enabled}
              fullDraftDisabled={!form.values.enabled}
            />
          </Table.ScrollContainer>
        </Group>
        <Group justify="right">
          <Button
            variant="subtle"
            leftSection={<FaIcon faIconSet="fas" icon="fa-plus" />}
            onClick={handleNewRevisionSet}
            disabled={!form.values.enabled}
          >
            New revision set
          </Button>
        </Group>
      </Stack>
      <Divider />
    </ModalForm>
  )
}

interface RevisionStatusMessageProps {
  initiallyEnabled?: boolean
  values: Pick<FormState, 'available' | 'active'>
}

function RevisionStatusMessage(props: RevisionStatusMessageProps) {
  'use memo'

  const { available, active } = props.values
  const hasSelection = available instanceof SelectList
  const label = hasSelection ? available.selected.label : active.label

  if (props.initiallyEnabled === false) {
    return (
      <Text c="dimmed">
        You&apos;ll enter <strong>{label}</strong> as soon as you click{' '}
        <strong>Save</strong>.
      </Text>
    )
  }

  if (hasSelection) {
    return (
      <Text c="dimmed">
        You&apos;ll switch to <strong>{label}</strong> as soon as you click{' '}
        <strong>Save</strong>.
      </Text>
    )
  }

  return (
    <Text c="dimmed">
      You&apos;re currently on <strong>{label}</strong>
    </Text>
  )
}

interface ControlledRevisionsModalProps {
  revisionsState: RevisionsState | undefined
  revisionsInvalid: boolean
  scriptId: string
  trackingChanges: boolean
  opened: boolean
  onClose(): void
  resetRevisions(): void
}

// This is the start of the boundary that is not aware at all of MST. It's
// called by an `observer` function, so we need to make sure it's called with
// referentially-stable values for React.memo to work
const ControlledRevisionsModal = React.memo(function ControlledRevisionsModal(
  props: ControlledRevisionsModalProps,
) {
  'use memo'

  const [errorMessage, setErrorMessage] = React.useState<ReactNode>()

  function handleClose() {
    setErrorMessage(undefined)
    props.onClose()
  }

  const updateRevisionsMutation = nido.scripts.updateRevisions.useMutation({
    onSuccess() {
      handleClose()
    },
    onError(err) {
      const knownError = nidoErrorSchema.safeParse(err)
      if (knownError.data?.status === 422) {
        setErrorMessage(knownError.data.body.message)
      } else {
        setErrorMessage('Could not save revisions at this time.')
      }
    },
  })

  function handleConfirm(values: FormState) {
    updateRevisionsMutation.mutate({
      params: { scriptId: props.scriptId },
      body: values,
    })
  }

  function handleResetRevisions() {
    props.resetRevisions()
    handleClose()
  }

  const modalErrorMessage = props.revisionsInvalid ? (
    <Toast
      type="error"
      dismissable={false}
      message="These revisions are invalid"
    >
      <Button color="red" variant="outline" onClick={handleResetRevisions}>
        Reset revisions for this script
      </Button>
    </Toast>
  ) : (
    errorMessage
  )

  return (
    <RevisionsModal
      opened={props.opened}
      onClose={handleClose}
      errorMessage={modalErrorMessage}
      onConfirm={handleConfirm}
      loading={updateRevisionsMutation.isPending}
      initialRevisionState={props.revisionsState}
      changeTrackingActive={props.trackingChanges}
    />
  )
})

interface EditorRevisionsModalProps {
  opened: boolean
  onClose(): void
}

// This should remain the only mst-connected component in this file. The goal is
// to choke off the point where we depend on MST and just pass everything else
// down as vanilla props, so we follow the "Rules of Hooks" and memoize the rest
export const EditorRevisionsModal = observer(function EditorRevisionsModal(
  props: EditorRevisionsModalProps,
) {
  const { currentScript } = useMst()
  const editor = useEditor()

  const resetRevisions = React.useCallback(() => {
    editor.invoke((state, dispatch) => {
      dispatch?.(state.tr.setDocAttribute('revisions', null))
      return true
    })
  }, [editor])

  const editorState = currentScript?.pmEditor.editorState
  if (!editorState || !currentScript) return null

  // We are explicitly not using `revisions` from ILoadedScript as a part of refactor
  // to get rid of mst coupling. We want to access fields from `EditorState`
  // instance directly
  const revisions = getRevisionsState(editorState)
  const revisionsState =
    revisions.tag === 'Resolved' || revisions.tag === 'Loading'
      ? revisions.revisionsState
      : undefined
  const revisionsInvalid = revisions.tag === 'Error'

  return (
    <ControlledRevisionsModal
      revisionsState={revisionsState}
      revisionsInvalid={revisionsInvalid}
      scriptId={currentScript.id}
      trackingChanges={currentScript.trackingChanges}
      opened={props.opened}
      onClose={props.onClose}
      resetRevisions={resetRevisions}
    />
  )
})

function initialFormState(revisionState?: RevisionsState): FormState {
  const initialRevisions =
    revisionState?.revisions ?? RevisionsState.fromDefaults().revisions

  return {
    enabled: revisionState?.enabled ?? false,
    markOption: revisionState?.markOption ?? showRevisionMarkOptions[0].value,
    showPageColors: revisionState?.showPageColors ?? true,
    previous: [...initialRevisions.previous],
    active: initialRevisions.active,
    available: initialRevisions.available.map(addKey),
  }
}

function scrollToBottom(ref: React.RefObject<HTMLElement | null>) {
  if (!ref.current) return
  const position = ref.current.scrollHeight
  ref.current.scrollTo({ top: position, behavior: 'smooth' })
}

function makeKeyable(prefix = 'key') {
  let id = 0

  return function addKey<T extends object>(obj: T): WithKey<T> {
    return { ...obj, key: `${prefix}-${id++}` }
  }
}
